/**
 * Initialization
 *
 * Entry point for apps to configure the HTTP package. Must be called after
 * loadRuntimeConfig() and before any API requests.
 *
 * Boot sequence:
 * 1. loadRuntimeConfig()               — @erebus/runtime-config
 * 2. init() or init({ bearerToken })   — this module
 * 3. onAuthFailure(handler)            — register logout/redirect callback
 * 4. [app-specific interceptors]       — connection monitoring, inactivity, etc.
 * 5. await restoreSession()            — check for existing session cookie
 *
 * Two auth modes:
 * - Cookie mode (default): erebusHttp sends the session cookie; legacyHttp
 *   gets its token from the legacy token manager.
 * - Bearer mode (bearerToken given): both instances send the app's own token
 *   as an Authorization header, and no cookies are sent.
 */

import type { InternalAxiosRequestConfig } from 'axios';
import { getRuntimeConfig } from '@erebus/runtime-config';
import { erebusHttp, legacyHttp } from './instances';
import {
  installLegacyInterceptors,
  restoreLegacyToken,
  setAuthFailureHandler,
} from './legacy-token-manager';
import type { AuthFailureHandler, SessionRestoreResult } from './types';

/** Options for init(). */
export interface InitOptions {
  /**
   * Turns on Bearer mode. Called on every request; the returned token is sent
   * as `Authorization: Bearer <token>`. Return null or '' to send no header.
   */
  bearerToken?: () => string | null;
}

let initialized = false;
let bearerMode = false;

/** Ids of the Bearer request interceptors, so resetInit() can remove them. */
let bearerInterceptorIds: { erebus: number; legacy: number } | null = null;

/**
 * Configures both axios instances with base URLs from runtime-config and sets
 * up auth. Call once at app startup.
 *
 * Without options (cookie mode) it installs the legacy token interceptors.
 * With `bearerToken` (Bearer mode) it adds one request interceptor per
 * instance instead, and turns off cookies on erebusHttp. The backend checks
 * the session cookie before the Bearer header, so a leftover cookie would
 * otherwise win over the token.
 *
 * @throws If called before loadRuntimeConfig()
 */
export function init(options: InitOptions = {}): void {
  if (initialized) return;

  const config = getRuntimeConfig();

  erebusHttp.defaults.baseURL = config.apiBaseUrl;
  legacyHttp.defaults.baseURL = config.legacyApiBaseUrl;

  const { bearerToken } = options;
  if (bearerToken) {
    const attachBearerToken = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      const token = bearerToken();
      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
      }
      return request;
    };

    erebusHttp.defaults.withCredentials = false;
    bearerInterceptorIds = {
      erebus: erebusHttp.interceptors.request.use(attachBearerToken),
      legacy: legacyHttp.interceptors.request.use(attachBearerToken),
    };
    bearerMode = true;
  } else {
    installLegacyInterceptors(legacyHttp);
  }

  initialized = true;
}

/**
 * Registers a callback invoked when authentication fails irrecoverably
 * (session cookie expired, legacy token refresh failed after retry).
 * Apps use this to redirect to the login page.
 */
export function onAuthFailure(handler: AuthFailureHandler): void {
  setAuthFailureHandler(handler);
}

/**
 * Attempts to restore an existing session by fetching the legacy token
 * using the session cookie.
 *
 * Returns 'ok' if the session is valid, 'no-session' if there is none, and
 * 'offline' if the server could not be reached. Only 'no-session' calls the
 * auth failure handler. Compare the result with `=== 'ok'`: every value is a
 * non-empty string, so `if (await restoreSession())` is always true.
 *
 * Call during app bootstrap (after init()) instead of reading from localStorage.
 * Always 'no-session' in Bearer mode, which has no session cookie to restore.
 */
export async function restoreSession(): Promise<SessionRestoreResult> {
  if (bearerMode) return 'no-session';
  return restoreLegacyToken();
}

/**
 * Resets initialization state. Removes the Bearer interceptors and turns
 * cookies back on. For tests only.
 */
export function resetInit(): void {
  if (bearerInterceptorIds) {
    erebusHttp.interceptors.request.eject(bearerInterceptorIds.erebus);
    legacyHttp.interceptors.request.eject(bearerInterceptorIds.legacy);
    bearerInterceptorIds = null;
  }
  erebusHttp.defaults.withCredentials = true;
  bearerMode = false;
  initialized = false;
}
